import { ReactComponent as ApMark } from '@rocco/icons/svg/archipro-receipt-mark.svg';
import { ProfessionalTileLogo } from '@rocco/ui/tile-professional/components/ProfessionalTileLogo';
import {
    gatePaneImageAttrs,
    type GatePaneImage,
} from '~/modules-rocco/user/auth-gate/gate-pane-image';

/** First names read as a person talking; full names read as a record. */
const firstNameOf = (fullName: string): string =>
    fullName.trim().split(/\s+/)[0] || fullName;

interface BookMeetingGatePaneProps {
    /** Behind everything: the person's own photo when there is one. */
    image: GatePaneImage;
    /** Who the reader is meeting. */
    subjectName: string;
    /** The practice, named as the party who confirms. */
    professionalName: string;
    /**
     * No public team member to meet, so the promise is the practice's rather
     * than a person's. The pane has already named them a line above, so this
     * says "their team" instead of repeating the practice inside its own block.
     *
     * Null while availability is still loading: who confirms the meeting is the
     * one thing on this pane the page cannot know on its own, so the shared
     * half of the line stands alone until the answer arrives rather than
     * guessing and correcting itself a second later.
     */
    isCompany: boolean | null;
    /** Their calendar is connected, so the time is taken rather than proposed. */
    isBooked?: boolean;
    /** Their logo, badged above the name so the practice is placed as well as named. */
    companyLogo?: string | null;
    logoBackgroundColor?: string | null;
}

/**
 * Left pane of the booking gate: the person the reader asked to meet.
 *
 * The default brand pane sells ArchiPro, which is the right pitch when the wall
 * is the point. Here the reader has already chosen someone, so the pane holds
 * who that is — and carries the supporting line, which frees the right-hand
 * column for the calendar rather than spending three lines of it on prose.
 *
 * Desktop only: TwoPaneModal drops the pane on the sheet, so nothing here may
 * be the only place something is said. See BC-5174.
 */
export const BookMeetingGatePane = ({
    image,
    subjectName,
    professionalName,
    isCompany,
    isBooked = false,
    companyLogo,
    logoBackgroundColor,
}: BookMeetingGatePaneProps) => (
    <div className="relative size-full">
        <img
            {...gatePaneImageAttrs(image)}
            alt=""
            className="absolute inset-0 size-full object-cover"
        />
        {/* Heavier at the foot than the brand pane's flat wash: the statement
            sits over a photograph of a person, where a single face can put any
            part of the frame light behind the type. */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/65" />
        <ApMark className="absolute top-10 left-7 h-auto w-28 [&_path]:fill-white" />

        <div className="absolute bottom-12.5 left-7 w-95.5">
            {/* The practice's badge leads the block, so the eye reads whose
                office this is before whose name it is. Same circular badge the
                tiles and the profile header use, rather than a bespoke chip. */}
            {companyLogo ? (
                <ProfessionalTileLogo
                    className="mb-5 size-20"
                    image={{ src: companyLogo, alt: professionalName }}
                    background={logoBackgroundColor ?? null}
                    contrastBorder
                />
            ) : null}
            <p className="m-0 text-[1.75rem] leading-[0.975] font-medium tracking-[-0.03em] text-white">
                Meet with {subjectName}
            </p>
            <p className="m-0 mt-3 text-[0.8125rem] leading-snug text-white/80">
                {/* Unknown reads as the practice's promise rather than as half a
                    sentence: it is the common case, and both versions run to two
                    lines, so the block keeps its height when the answer lands.
                    Dropping the second sentence while loading was worse — the
                    paragraph grew a line and shunted the heading up. */}
                {isBooked
                    ? `Pick a day and a time that suit. It goes straight into ${firstNameOf(subjectName)}'s calendar.`
                    : (isCompany ?? true)
                      ? 'Pick a day and a time that suit. Their team will be in touch to confirm your appointment.'
                      : `Pick a day and a time that suit. ${firstNameOf(subjectName)} confirms it with you, or suggests another.`}
            </p>
        </div>
    </div>
);
